import { api } from "@/lib/api";
import {
  mapDtoToService,
  ServiceDto,
  serviceDtoListSchema,
  serviceDtoSchema,
} from "@/models";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useToast } from "../use-toast";

export const getServicesQueryOptions = queryOptions({
  queryKey: ["services"],
  queryFn: async () => {
    const result: Array<ServiceDto> = [];
    let page = 0;
    let hasNextPage = true;
    while (hasNextPage) {
      page += 1;
      const res = await api.getWithCache("/api/services", serviceDtoListSchema, {
        query: {
          page: page.toString(),
        },
      });
      result.push(...res.Data);
      hasNextPage = res.PageInfo.HasNextPage;
    }
    return result.map(mapDtoToService);
  },
});

export function useGetServices<TERROR extends Error>(opts?: {
  onError?: (error: TERROR) => void;
  enabled?: boolean;
}) {
  const { toast } = useToast();

  const q = useQuery({
    ...getServicesQueryOptions,
    enabled: opts?.enabled,
  });
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getServicesError",
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}

export function getServiceQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["services", id],
    queryFn: () =>
      api
        .get("/api/services/$id", serviceDtoSchema, {
          pathReplace: { $id: id },
        })
        .then(mapDtoToService),
  });
}

export function useGetService<TERROR extends Error>(
  id: string,
  opts?: { onError?: (error: TERROR) => void; enabled?: boolean },
) {
  const { toast } = useToast();
  const q = useQuery({
    ...getServiceQueryOptions(id),
    enabled: opts?.enabled,
  });
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getServiceDetailError",
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}
